
import Vue from "vue";
import * as selectOptions from "@/helpers/selectOptions";
import Help from "@/components/Help/Inquiries.vue";
import GoogleAutoComplete from "@/components/User/GoogleAutoComplete.vue";
import { Session } from "@/helpers/processSession";
import { quoteMapActions } from "@/store/modules/quote";
import { propertyChangeRequestMapActions } from "@/store/modules/propertychangerequest";
import { IAddress } from "@/types";
import { defaultAddressStructure } from "@/helpers/defaultObjects";
import BaseCollectionService from "@/services/base.collection.service";
import RiskAddressScrubbingFailedModal from "@/views/shared/quotes/RiskAddressScrubbingFailedModal.vue";
import { extractAddressDataFromGoogleAutoComplete } from "@/helpers/addressFormatter";
import CountyAvailabilityDropdown from "@/components/Quotes/CountyAvailabilityDropdown.vue";
interface IQuotesData {
  countyValidityCheckFormValues: Record<string, any>;
  session: Session;
  isLoading: boolean;
  scrubbedAddress: IAddress;
  countyAvailabilityResult: {
    allowed: { companyNumber: number; isBlocked: boolean }[];
    blocked: { companyNumber: number; isBlocked: boolean }[];
  };
  showAddressValidationFailedModal: boolean;
}

export default Vue.extend({
  name: "CountyAvailabilityCheck",
  components: {
    Help,
    GoogleAutoComplete,
    RiskAddressScrubbingFailedModal,
    CountyAvailabilityDropdown
  },
  data(): IQuotesData {
    return {
      countyValidityCheckFormValues: {
        effectiveDate: new Date(),
        lineOfBusiness: "",
        address: ""
      },
      session: new Session(),
      isLoading: false,
      scrubbedAddress: defaultAddressStructure,
      countyAvailabilityResult: {
        allowed: [],
        blocked: []
      },
      showAddressValidationFailedModal: false
    };
  },
  methods: {
    ...quoteMapActions(["createQuote"]),
    ...propertyChangeRequestMapActions(["scrubAddress"]),
    async setCountyData(county: string) {
      if (!county) {
        this.$appNotifyError("County is required");
        return;
      }
      this.scrubbedAddress.county = county;
      await this.handleCountyVisibilityCheck();
    },

    handleAddressScrubbingFailed() {
      this.showAddressValidationFailedModal = false;
      this.handleCountyVisibilityCheckModal("show");
      if (this?.$refs?.addressAutoComplete) {
        const addressAutoComplete = this.$refs.addressAutoComplete as Vue & {
          $el: HTMLElement;
        };
        addressAutoComplete.$el.focus();
      }
    },
    handleCloseCheckCountyAvailabilityModal() {
      this.handleCountyVisibilityCheckModal("hide");
      this.resetForm();
    },
    handlePlaceChanged(newAddress: any) {
      const googleVerifiedAddress = extractAddressDataFromGoogleAutoComplete(
        newAddress
      );
      this.scrubbedAddress = {
        ...defaultAddressStructure,
        ...googleVerifiedAddress
      };
    },
    resetForm() {
      this.countyValidityCheckFormValues = {
        address: "",
        lineOfBusiness: "",
        effectiveDate: new Date()
      };
    },
    goBack() {
      this.resetForm();
      this.$modal.hide("countyAvailabilityResultModal");
      this.$modal.show("countyAvailabilityModal");
    },
    handleCountyVisibilityCheckModal(action: "hide" | "show") {
      const modalName = "countyAvailabilityModal";
      this.toggleModal(modalName, action);
    },
    handleCheckCountyAvailabilityModal() {
      this.handleCountyVisibilityCheckModal("show");
    },
    async handleCountyVisibilityCheck() {
      try {
        this.isLoading = true;
        const request = new BaseCollectionService("county-validity-check");
        const response = await request.createOne({
          effectiveDate: this.countyValidityCheckFormValues.effectiveDate,
          address: {
            ...this.scrubbedAddress
          }
        });
        this.countyAvailabilityResult = response?.data || [];
        this.handleCountyVisibilityCheckModal("hide");
        this.handleCountyVisibilityResultModal("show");
        this.showAddressValidationFailedModal = false;
      } catch (error) {
        this.$appNotifyError(
          "An error occurred while checking county availability"
        );
      } finally {
        this.isLoading = false;
      }
    },
    handleCountyVisibilityResultModal(action: "hide" | "show") {
      const modalName = "countyAvailabilityResultModal";
      this.toggleModal(modalName, action);
    },
    handleCloseCountyVisibilityResultModal() {
      this.handleCountyVisibilityResultModal("hide");
      this.resetForm();
    },
    async doScrubAddress() {
      try {
        this.isLoading = true;
        const address = await this.scrubAddress({
          formattedAddress: this.countyValidityCheckFormValues.address
        });

        this.scrubbedAddress = {
          ...defaultAddressStructure,
          ...address
        };
        await this.handleCountyVisibilityCheck();
      } catch (error) {
        this.$bugSnagClient.notify(error);
        this.handleCountyVisibilityCheckModal("hide");
        this.showAddressValidationFailedModal = true;
      } finally {
        this.isLoading = false;
      }
    },
    handleCountyValidationFormFieldChange(value: string, field: string) {
      this.countyValidityCheckFormValues[field] = value;
    },
    async handleCreateQuote(row: any) {
      try {
        const payload = {
          policyType: this.countyValidityCheckFormValues.lineOfBusiness,
          companyNumber: row?.companyNumber
        };

        const response = await this.createQuote(payload);
        this.session.set("countyValidityCheckFormValues", {
          address: this.scrubbedAddress,
          effectiveDate: this.countyValidityCheckFormValues.effectiveDate
        });
        this.resetForm();
        this.$router.push(`/quotes/${response._id}`);
      } catch (error) {
        this.$appNotifyError(
          error.message || "An error occurred while creating the quote"
        );
      }
    },
    formatCountyAvailabilityData(isAvailable: boolean = false) {
      return isAvailable
        ? this.countyAvailabilityResult.allowed
        : this.countyAvailabilityResult.blocked;
    },
    toggleModal(modalName: string, action: "hide" | "show") {
      if (action === "show") {
        this.$modal.show(modalName);
      } else if (action === "hide") {
        this.$modal.hide(modalName);
      } else {
        this.$appNotifyError("Invalid action for modal toggle");
      }
    }
  },
  computed: {
    disableCountyAvailabilityCheckButton(): boolean {
      return (
        !this.countyValidityCheckFormValues.address ||
        !this.countyValidityCheckFormValues.lineOfBusiness ||
        !this.countyValidityCheckFormValues.effectiveDate
      );
    },
    getCountyValidityDescription(): string {
      return `Below are the county availability results for ${this
        ?.scrubbedAddress?.county || "N/A"}`;
    },
    lineOfBusinessOptions(): { label: string; value: string }[] {
      return selectOptions.companyLineOfBusiness;
    }
  }
});
